// src/layouts/LoggedInLayout/Components/Header/Header.tsx
import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiSearch, FiBell, FiMenu, FiSettings, FiLogOut } from "react-icons/fi";
import { RiSunFill, RiMoonFill } from "react-icons/ri";
import { HiChevronDown } from "react-icons/hi";
import { useThemeStore } from '../../../stores/useThemeStore';
import { useAuthStore } from '../../../stores/useAuthStore';
import useNotificationStore from '../../../stores/NotificationStore';
import NotificationCenter from '../../../Components/NotificationCenter/NotificationCenter';
import logo from "../../../assets/Header/mile.png"

interface HeaderProps {
  isMobileMenuOpen: boolean;
  setIsMobileMenuOpen: (open: boolean) => void;
}

const Header: React.FC<HeaderProps> = ({ isMobileMenuOpen, setIsMobileMenuOpen }) => {
  const { theme, toggleTheme } = useThemeStore();
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const isDarkMode = theme === 'dark';
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const profileMenuRef = useRef<HTMLDivElement>(null);
  const [showNotifications, setShowNotifications] = useState(false);
  const { unreadCount, fetchUnreadCount } = useNotificationStore();

  const toggleMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const toggleProfileMenu = () => {
    setIsProfileMenuOpen(!isProfileMenuOpen);
  };
  // Fetch unread count on mount
  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 60000); // Refresh every minute
    return () => clearInterval(interval);
  }, [fetchUnreadCount]);

  const handleAccountSettings = () => {
    navigate('/account-settings');
    setIsProfileMenuOpen(false);
  };

  const handleSignOut = () => {
    // Clear any auth data if needed
    navigate('/login');
    setIsProfileMenuOpen(false);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target as Node)) {
        setIsProfileMenuOpen(false);
      }
    };

    if (isProfileMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isProfileMenuOpen]);

  return (
    <>
      <style>{`
        .logged-in-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 0.5rem 2rem;
          border-radius: 2rem;
          background-color: var(--color-white, #ffffff);
          position: relative;
        }
        [data-theme="dark"] .logged-in-header {
          background-color: var(--color-primary-dark, #262B45);
        }

        .logged-in-header-left { 
          flex: 0 0 auto;
          display: flex;
          align-items: center;
          gap: 12px;
        }
        
        .logged-in-hamburger-btn {
          display: none;
          background: none;
          border: none;
          cursor: pointer;
          color: #1f2937;
          padding: 8px;
        }
        [data-theme="dark"] .logged-in-hamburger-btn {
          color: var(--color-white, #ffffff);
        }
        .logged-in-logo-img {
          width: 40px;
          height: 40px;
          border-radius: 12px;
          object-fit: contain;
          padding: 4px;
          transition: transform 0.2s ease;
        }
        .logged-in-logo { 
          display: flex; 
          align-items: center; 
          gap: 8px; 
        }
        .logged-in-logo-icon { 
          width: 36px; 
          height: 36px; 
          border-radius: 50%; 
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-weight: bold;
        }
        .logged-in-logo-text {
          font-size: 1.5rem;
          font-weight: 600;
          color: #f97316;
        }
        [data-theme="dark"] .logged-in-logo-text {
          color: var(--color-sunflare, #FEB901);
        }

        .logged-in-header-center { 
          flex: 1; 
          max-width: 400px;
          margin: 0 2rem;
        }
        .logged-in-search-bar {
          display: flex;
          align-items: center;
          background-color: #FFF8F0;
          border: 0.6px solid #F4D06F;
          border-radius: 24px;
          padding: 8px 16px;
          gap: 8px;
        }
        .logged-in-search-bar:focus-within {
          background-color: #FFEFD9;
          border-color: #F1C456;
        }
        [data-theme="dark"] .logged-in-search-bar {
          background-color: #353B55;
          border-color: #4A5568;
        }
        [data-theme="dark"] .logged-in-search-bar:focus-within {
          background-color: #3C4261;
          border-color: #FEB901;
        }
        .logged-in-search-icon {
          color: var(--color-primary, #f97316);
          flex-shrink: 0;
        }
        [data-theme="dark"] .logged-in-search-icon {
          color: var(--color-sunflare, #FEB901);
        }
        .logged-in-search-input {
          border: none;
          outline: none;
          width: 100%;
          background-color: transparent;
          font-size: 14px;
          color: #374151;
        }
        [data-theme="dark"] .logged-in-search-input {
          color: var(--color-white, #ffffff);
        }
        .logged-in-search-input::placeholder {
          color: var(--color-primary, #f97316);
        }
        [data-theme="dark"] .logged-in-search-input::placeholder {
          color: #9ca3af;
        }

        .logged-in-header-right { 
          display: flex; 
          align-items: center; 
          gap: 16px; 
        }
        
        .logged-in-toggle-switch {
          width: 60px;
          height: 30px;
          background-color: ${isDarkMode ? '#5EEAD4' : '#f97316'};
          border-radius: 3rem;
          position: relative;
          cursor: pointer;
          transition: background-color 0.3s ease;
        }

        .logged-in-toggle-slider {
          width: 26px;
          height: 26px;
          background-color: white;
          border-radius: 50%;
          position: absolute;
          top: 2px;
          left: ${isDarkMode ? '32px' : '2px'};
          transition: left 0.3s ease;
          display: flex;
          align-items:center;
          justify-content:center;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
        }

        .toggle-icon {
          font-size: 14px;
          color: ${isDarkMode ? '#1A1A1A' : '#f97316'};
        }

        .logged-in-notification-btn {
          position: relative;
          background-color: #1f2937;
          border: none;
          border-radius: 50%;
          width: 40px;
          height: 40px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: background-color 0.2s;
          color: white;
          flex-shrink: 0;
        }
        .logged-in-notification-btn:hover {
          background-color: #374151;
        }
        .notification-badge {
          position: absolute;
          top: -4px;
          right: -4px;
          background: #ff4444;
          color: white;
          border-radius: 10px;
          padding: 2px 6px;
          font-size: 11px;
          font-weight: 600;
          min-width: 18px;
          text-align: center;
        }
        [data-theme="dark"] .logged-in-notification-btn {
          background-color: #353B55;
          color: var(--color-sunflare, #FEB901);
        }
        [data-theme="dark"] .logged-in-notification-btn:hover {
          background-color: #3C4261;
        }

        .logged-in-user-profile {
          display: flex;
          align-items: center;
          gap: 8px;
          cursor: pointer;
          flex-shrink: 0;
          position: relative;
          padding: 4px 8px;
          border-radius: 12px;
          transition: background-color 0.2s;
        }
        .logged-in-user-profile:hover {
          background-color: #f3f4f6;
        }
        [data-theme="dark"] .logged-in-user-profile:hover {
          background-color: #353B55;
        }
        .logged-in-user-avatar {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          object-fit: cover;
        }
        .logged-in-user-name {
          font-size: 14px;
          font-weight: 500;
          color: #1f2937;
        }
        [data-theme="dark"] .logged-in-user-name {
          color: var(--color-white, #ffffff);
        }
        .logged-in-dropdown-icon {
          color: #6b7280;
          transition: transform 0.2s;
        }
        .logged-in-dropdown-icon.open {
          transform: rotate(180deg);
        }
        [data-theme="dark"] .logged-in-dropdown-icon {
          color: #9ca3af;
        }

        /* Profile Dropdown Menu */
        .profile-dropdown-menu {
          position: absolute;
          top: calc(100% + 8px);
          right: 0;
          background: white;
          border-radius: 16px;
          box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
          min-width: 280px;
          z-index: 1000;
          opacity: 0;
          transform: translateY(-10px);
          pointer-events: none;
          transition: opacity 0.2s ease, transform 0.2s ease;
        }
        [data-theme="dark"] .profile-dropdown-menu {
          background: #353B55;
          box-shadow: 0 10px 40px rgba(0, 0, 0, 0.5);
        }
        .profile-dropdown-menu.open {
          opacity: 1;
          transform: translateY(0);
          pointer-events: all;
        }

        .profile-dropdown-header {
          padding: 20px;
          border-bottom: 1px solid #e5e7eb;
          display: flex;
          align-items: center;
          gap: 12px;
        }
        [data-theme="dark"] .profile-dropdown-header {
          border-bottom-color: #4A5568;
        }

        .profile-dropdown-avatar {
          width: 56px;
          height: 56px;
          border-radius: 50%;
          object-fit: cover;
        }

        .profile-dropdown-info {
          flex: 1;
        }

        .profile-dropdown-name {
          font-size: 16px;
          font-weight: 600;
          color: #1f2937;
          margin: 0 0 4px 0;
        }
        [data-theme="dark"] .profile-dropdown-name {
          color: var(--color-white, #ffffff);
        }

        .profile-dropdown-email {
          font-size: 14px;
          color: #6b7280;
          margin: 0;
        }
        [data-theme="dark"] .profile-dropdown-email {
          color: #9ca3af;
        }

        .profile-dropdown-body {
          padding: 8px;
        }

        .profile-dropdown-item {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 12px 16px;
          border-radius: 12px;
          cursor: pointer;
          transition: background-color 0.2s;
          color: #1f2937;
          text-decoration: none;
          border: none;
          background: none;
          width: 100%;
          text-align: left;
          font-size: 15px;
        }
        [data-theme="dark"] .profile-dropdown-item {
          color: var(--color-white, #ffffff);
        }
        .profile-dropdown-item:hover {
          background-color: #f3f4f6;
        }
        [data-theme="dark"] .profile-dropdown-item:hover {
          background-color: #3C4261;
        }

        .profile-dropdown-item-icon {
          width: 20px;
          height: 20px;
          color: #6b7280;
        }
        [data-theme="dark"] .profile-dropdown-item-icon {
          color: #9ca3af;
        }

        .profile-dropdown-item.sign-out {
          color: #ef4444;
        }
        .profile-dropdown-item.sign-out .profile-dropdown-item-icon {
          color: #ef4444;
        }

        /* Responsive styles for screens below 900px */
        @media (max-width: 990px) {
          .logged-in-header {
            padding: 0.5rem 1rem;
          }

          .logged-in-hamburger-btn {
            display: block;
          }

          .logged-in-header-center {
            display: none;
          }

          .logged-in-header-right {
            gap: 12px;
          }

          .logged-in-user-name {
            display: none;
          }

          .profile-dropdown-menu {
            right: 0;
          }
        }

        @media (max-width: 600px) {
          .logged-in-header {
            padding: 0.5rem 0.75rem;
          }

          .logged-in-header-right {
            gap: 8px;
          }

          .profile-dropdown-menu {
            min-width: 260px;
          }
        }

        @media (max-width: 500px) {
          .logged-in-header {
            padding: 0.4rem 0.5rem;
          }

          .logged-in-hamburger-btn {
            padding: 4px;
          }

          .logged-in-logo-icon {
            width: 28px;
            height: 28px;
          }

          .logged-in-logo-text {
            font-size: 1.125rem;
          }

          .logged-in-toggle-switch {
            width: 48px;
            height: 24px;
          }

          .logged-in-toggle-slider {
            width: 20px;
            height: 20px;
            left: ${isDarkMode ? '26px' : '2px'};
          }

          .toggle-icon {
            font-size: 11px;
          }

          .logged-in-notification-btn {
            width: 32px;
            height: 32px;
          }

          .logged-in-user-avatar {
            width: 32px;
            height: 32px;
          }

          .logged-in-dropdown-icon {
            width: 14px;
            height: 14px;
          }

          .logged-in-header-right {
            gap: 6px;
          }

          .logged-in-header-left {
            gap: 8px;
          }

          .profile-dropdown-menu {
            min-width: 240px;
            right: -8px;
          }
        }
      `}</style>

      <div className="logged-in-header">
        {/* Left - Hamburger Menu + Logo */}
        <div className="logged-in-header-left">
          <button 
            className="logged-in-hamburger-btn"
            onClick={toggleMenu}
            aria-label="Open menu"
          >
            <FiMenu size={24} />
          </button>
          
          <div className="logged-in-logo">
            <img src={logo} alt="Miles Logo" className="logged-in-logo-img" />
            <span className="logged-in-logo-text">Magnetix</span>
          </div>
        </div>

        {/* Center - Search Bar (hidden on mobile) */}
        <div className="logged-in-header-center">
          <div className="logged-in-search-bar">
            <FiSearch size={20} className="logged-in-search-icon" />
            <input
              type="text"
              placeholder="Search"
              className="logged-in-search-input"
            />
          </div>
        </div>

        {/* Right - Theme, Notifications, Profile */}
        <div className="logged-in-header-right">
          {/* Theme Toggle */}
          <div className="logged-in-theme-toggle" onClick={toggleTheme}>
            <div className="logged-in-toggle-switch">
              <div className="logged-in-toggle-slider">
                {isDarkMode ? (
                  <RiMoonFill className="toggle-icon" />
                ) : (
                  <RiSunFill className="toggle-icon" />
                )}
              </div>
            </div>
          </div>

          {/* Notifications */}
          <button 
            className="logged-in-notification-btn"
            onClick={() => setShowNotifications(!showNotifications)}
          >
            <FiBell size={20} />
            {unreadCount > 0 && (
              <span className="notification-badge">
                {unreadCount > 99 ? '99+' : unreadCount}
              </span>
            )}
          </button>

          <NotificationCenter 
            isOpen={showNotifications}
            onClose={() => setShowNotifications(false)}
          />

          {/* User Profile with Dropdown */}
          <div ref={profileMenuRef}>
            <div className="logged-in-user-profile" onClick={toggleProfileMenu}>
              <img
                src={user?.avatar ? `${process.env.REACT_APP_SOCKET_URL}/uploads/users/profile_picture/${user.avatar}` : "https://via.placeholder.com/100?text=" + (user?.name?.charAt(0) || 'U')}
                alt={user?.name || 'User'}
                className="logged-in-user-avatar"
              />
              <span className="logged-in-user-name">{user?.name || 'User'}</span>
              <HiChevronDown
                size={16}
                className={`logged-in-dropdown-icon ${isProfileMenuOpen ? 'open' : ''}`}
              />
            </div>

            {/* Dropdown Menu */}
            <div className={`profile-dropdown-menu ${isProfileMenuOpen ? 'open' : ''}`}>
              <div className="profile-dropdown-header">
                <img
                  src={user?.avatar ? `${process.env.REACT_APP_SOCKET_URL}/uploads/users/profile_picture/${user.avatar}` : "https://via.placeholder.com/100?text=" + (user?.name?.charAt(0) || 'U')}
                  alt={user?.name || 'User'}
                  className="profile-dropdown-avatar"
                />
                <div className="profile-dropdown-info">
                  <h3 className="profile-dropdown-name">{user?.name || 'User'}</h3>
                  <p className="profile-dropdown-email">{user?.email || ''}</p>
                </div>
              </div>

              <div className="profile-dropdown-body">
                <button className="profile-dropdown-item" onClick={handleAccountSettings}>
                  <FiSettings className="profile-dropdown-item-icon" size={20} />
                  Account Settings
                </button>

                <button className="profile-dropdown-item sign-out" onClick={handleSignOut}>
                  <FiLogOut className="profile-dropdown-item-icon" size={20} />
                  Sign Out
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Header;